import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import DOMPurify from 'dompurify';

interface TicketEditorProps {
    title: string;
    content: string;
    onTitleChange: (title: string) => void;
    onContentChange: (content: string) => void;
    titlePlaceholder?: string;
    contentPlaceholder?: string;
    autoFocus?: boolean;
}

export default function TicketEditor({
    title,
    content,
    onTitleChange,
    onContentChange,
    titlePlaceholder,
    contentPlaceholder,
    autoFocus = true,
}: TicketEditorProps): JSX.Element {
    // const { t } = useTranslation();
    const titleRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isTitleFocused, setIsTitleFocused] = useState(false);
    const [isContentFocused, setIsContentFocused] = useState(false);

    useEffect(() => {
        if (titleRef.current && titleRef.current.textContent !== title) {
            titleRef.current.textContent = title;
        }
    }, [title]);

    useEffect(() => {
        if (contentRef.current) {
            const sanitized = DOMPurify.sanitize(content);
            if (contentRef.current.innerHTML !== sanitized) {
                contentRef.current.innerHTML = sanitized;
            }
        }
    }, [content]);

    useEffect(() => {
        if (!autoFocus) return;

        // Use setTimeout to ensure the content is rendered first
        const timer = setTimeout(() => {
            if (titleRef.current) {
                titleRef.current.focus();
                // Move cursor to end of title
                const range = document.createRange();
                const selection = window.getSelection();

                if (titleRef.current.childNodes.length > 0) {
                    const lastNode = titleRef.current.childNodes[titleRef.current.childNodes.length - 1];
                    const length = lastNode.textContent?.length || 0;
                    range.setStart(lastNode, length);
                    range.collapse(true);
                    selection?.removeAllRanges();
                    selection?.addRange(range);
                } else if (titleRef.current.textContent) {
                    // Fallback: if no child nodes but has text content
                    const textNode = document.createTextNode(titleRef.current.textContent);
                    titleRef.current.innerHTML = '';
                    titleRef.current.appendChild(textNode);
                    const length = textNode.textContent?.length || 0;
                    range.setStart(textNode, length);
                    range.collapse(true);
                    selection?.removeAllRanges();
                    selection?.addRange(range);
                }
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [autoFocus]);

    const handleTitleInput = () => {
        if (titleRef.current) {
            onTitleChange(titleRef.current.textContent || '');
        }
    };

    const handleContentInput = () => {
        if (contentRef.current) {
            onContentChange(contentRef.current.innerHTML || '');
        }
    };

    const handleContentKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        // Move to title on up arrow if at the start
        if (e.key === 'ArrowUp') {
            const selection = window.getSelection();
            if (selection && contentRef.current) {
                const range = selection.getRangeAt(0);
                const isAtStart = range.startOffset === 0 && range.endOffset === 0;
                if (isAtStart) {
                    e.preventDefault();
                    titleRef.current?.focus();
                    // Move cursor to end of title
                    const newRange = document.createRange();
                    const newSelection = window.getSelection();
                    if (titleRef.current?.firstChild) {
                        const textNode = titleRef.current.firstChild;
                        const length = textNode.textContent?.length || 0;
                        newRange.setStart(textNode, length);
                        newRange.collapse(true);
                        newSelection?.removeAllRanges();
                        newSelection?.addRange(newRange);
                    }
                }
            }
        }
    };

    const handleTitleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            contentRef.current?.focus();
        }

        // Move to content on down arrow if at the end
        if (e.key === 'ArrowDown') {
            const selection = window.getSelection();
            if (selection && titleRef.current) {
                const range = selection.getRangeAt(0);
                const isAtEnd = range.endOffset === titleRef.current.textContent?.length;
                if (isAtEnd) {
                    e.preventDefault();
                    contentRef.current?.focus();
                    // Move cursor to start of content
                    const newRange = document.createRange();
                    const newSelection = window.getSelection();
                    if (contentRef.current?.firstChild) {
                        newRange.setStart(contentRef.current.firstChild, 0);
                        newRange.collapse(true);
                        newSelection?.removeAllRanges();
                        newSelection?.addRange(newRange);
                    }
                }
            }
        }
    };

    const handleTitlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');

        // Modern approach: use Selection API instead of deprecated execCommand
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        range.deleteContents();

        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // Move cursor to end of inserted text
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);

        // Trigger input event to update state
        handleTitleInput();
    };

    return (
        <div className="w-full">
            {/* Title */}
            <div className="relative">
                <div
                    ref={titleRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleTitleInput}
                    onKeyDown={handleTitleKeyDown}
                    onPaste={handleTitlePaste}
                    onFocus={() => setIsTitleFocused(true)}
                    onBlur={() => setIsTitleFocused(false)}
                    className={`
                        outline-none
                        text-4xl font-bold
                        mb-2
                        min-h-[3rem]
                        ${!title && !isTitleFocused ? 'text-gray-400' : 'text-gray-900'}
                    `}
                    data-placeholder={titlePlaceholder}
                    style={{
                        caretColor: 'rgb(55, 53, 47)',
                    }}
                />
            </div>

            {/* Content */}
            <div className="relative">
                <div
                    ref={contentRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleContentInput}
                    onKeyDown={handleContentKeyDown}
                    onFocus={() => setIsContentFocused(true)}
                    onBlur={() => setIsContentFocused(false)}
                    className={`
                        outline-none
                        text-base
                        leading-relaxed
                        min-h-[20rem]
                        ${!content && !isContentFocused ? 'text-gray-400' : 'text-gray-700'}
                        prose prose-sm max-w-none
                        [&_a]:text-blue-600 [&_a]:underline
                        [&_strong]:font-semibold
                        [&_em]:italic
                        [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-3
                        [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-2
                        [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2
                        [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2
                        [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2
                        [&_li]:my-1
                        [&_p]:my-2
                        [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic
                        [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:
                        [&_pre]:bg-gray-100 [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:my-4
                    `}
                    data-placeholder={contentPlaceholder}
                    style={{
                        caretColor: 'rgb(55, 53, 47)',
                    }}
                />
                {!content && !isContentFocused && (
                    <div className="text-gray-400 pointer-events-none absolute top-0 left-0 text-base">
                        {contentPlaceholder}
                    </div>
                )}
            </div>
        </div>
    );
}
